import { useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { areaClass, fieldClass } from '../../../components/ui/form-classes';
import {
  buildAssignmentOverrides,
  formatTaskWindowRange,
  overrideInputError,
} from '../../../core/conflict-checker';
import type { TaskWindow } from '../../../core/conflict-checker';
import {
  CONFLICT_SEVERITY_LABELS,
  CONFLICT_SEVERITY_TONES,
  OVERRIDE_REASONS,
  OVERRIDE_REASON_LABELS,
  OVERRIDE_REASON_REQUIRING_TEXT,
  SCHEDULE_CONFLICT_TYPE_LABELS,
} from '../../../types/assignment';
import type {
  AssignmentOverride,
  OverrideReasonCode,
  ScheduleConflict,
} from '../../../types/assignment';

interface OverrideDialogProps {
  /** Every warning still waiting for a decision — all of them are shown. */
  conflicts: readonly ScheduleConflict[];
  /** The task being crewed, so the dialog can quote its own time window. */
  task?: TaskWindow | null;
  /** The `User.id` of the manager taking responsibility. */
  managerId: string;
  onCancel: () => void;
  onConfirm: (overrides: AssignmentOverride[]) => void;
}

/**
 * The mandatory confirmation behind "שבץ בכל זאת".
 *
 * It shows the manager exactly what they are overriding — every warning, its
 * severity and the overlapping time range — and refuses to close until a
 * reason has been chosen. "סיבה אחרת" additionally requires the free text, so
 * the ledger never fills up with unexplained decisions. The rule itself is
 * asked of `src/core/conflict-checker.ts`; nothing is re-decided here.
 */
export function OverrideDialog({
  conflicts,
  task,
  managerId,
  onCancel,
  onConfirm,
}: OverrideDialogProps) {
  const [reason, setReason] = useState<OverrideReasonCode | ''>('');
  const [reasonText, setReasonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const needsText = reason === OVERRIDE_REASON_REQUIRING_TEXT;
  const blocked = overrideInputError({ reason, reasonText, managerId });

  const handleConfirm = () => {
    const problem = overrideInputError({ reason, reasonText, managerId });
    if (problem) {
      setError(problem);
      return;
    }
    onConfirm(buildAssignmentOverrides(conflicts, { reason, reasonText, managerId }));
  };

  return (
    <Modal
      title="שבץ בכל זאת — אישור אחריות"
      subtitle="השיבוץ מנוגד לנתוני המערכת. אישור הפעולה נרשם ביומן הביקורת."
      onClose={onCancel}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={Boolean(blocked)}
            className="h-10 px-5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
          >
            אישור ושיבוץ בכל זאת
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {task ? (
          <p className="text-sm text-ink/60">
            המשימה: <span className="font-medium text-ink">{task.title}</span> · שעות{' '}
            <span data-dir="ltr" className="tabular-nums">
              {formatTaskWindowRange(task)}
            </span>
          </p>
        ) : null}

        <section className="rounded-2xl border border-rose-200 bg-rose-50/60 p-3 space-y-2">
          <h3 className="text-xs font-bold text-rose-700 flex items-center gap-1.5">
            <ShieldAlert size={14} aria-hidden />
            ההתראות שאתה עוקף ({conflicts.length})
          </h3>
          <ul className="space-y-1.5">
            {conflicts.map((conflict) => (
              <li
                key={conflict.key}
                className="rounded-xl border border-line bg-panel p-2 text-sm flex items-start gap-2"
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-ink/50">
                      {SCHEDULE_CONFLICT_TYPE_LABELS[conflict.conflictType]}
                    </span>
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded-lg border ${CONFLICT_SEVERITY_TONES[conflict.severity]}`}
                    >
                      {CONFLICT_SEVERITY_LABELS[conflict.severity]}
                    </span>
                    {conflict.conflictingWindow ? (
                      <span
                        data-dir="ltr"
                        className="text-[11px] px-1.5 py-0.5 rounded-lg border border-line bg-canvas tabular-nums"
                      >
                        {conflict.conflictingWindow}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-ink/80">{conflict.details}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <label className="block">
          <span className="block text-xs text-ink/60 mb-1">סיבת השיבוץ בכל זאת — חובה</span>
          <select
            value={reason}
            onChange={(event) => {
              setReason(event.target.value as OverrideReasonCode | '');
              setError(null);
            }}
            aria-label="סיבת השיבוץ בכל זאת"
            className={fieldClass}
          >
            <option value="">— בחר סיבה —</option>
            {OVERRIDE_REASONS.map((code) => (
              <option key={code} value={code}>
                {OVERRIDE_REASON_LABELS[code]}
              </option>
            ))}
          </select>
        </label>

        {needsText ? (
          <label className="block">
            <span className="block text-xs text-ink/60 mb-1">פירוט הסיבה — חובה</span>
            <textarea
              value={reasonText}
              onChange={(event) => {
                setReasonText(event.target.value);
                setError(null);
              }}
              rows={2}
              aria-label="פירוט הסיבה"
              placeholder="לדוגמה: הרכב מוחזר מהמוסך בבוקר המשימה"
              className={areaClass}
            />
          </label>
        ) : null}

        <p className="text-xs text-ink/50">
          הפעולה תישמר ביומן הביקורת עם שם המשבץ, הסיבה והשעה, ותוצג על הצוות עצמו.
        </p>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
